import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ChannelPlayer from "core/components/ChannelPlayer";
import ChannelScreenContent from "core/components/ChannelScreenContent";
import ContentIsNotAvailableError from "core/components/ContentIsNotAvailableError";
import { useTranslation } from "react-i18next";

const CHANNEL = "CHANNEL";

function resolveChannelPlayerErrorTranslationKey(playerError) {
  switch (playerError?.type) {
    case "invalid_country":
      return "invalidCountry";
    case "invalid_host":
      return "invalidHost";
    case "browser_drm_compatibility":
      return "browserDrmCompatibility";
    case "no_stream":
      return "noStream";
    default:
      return "noChannel";
  }
}

export default function ChannelScreen({
  content,
  view,
  viewDataTracker,
  autoPlay,
  muted,
  hideTitle,
  closeRouteDialogButton,
  controls,
  fullscreenTarget,
  sponsorCornerBannerPlace,
  sponsorInStreamBannerPlace,
  sponsorPlayerLogoPlace,
  sponsorPlayerButtonPlace,
  sponsorVideoPlace,
}) {
  const { t } = useTranslation("core");
  const [videoState, setVideoState] = useState(null);
  const [playerError, setPlayerError] = useState(null);
  const videoActionsRef = useRef(null);

  const handleVideoStateChange = useCallback(
    (newVideoState, newVideoActions) => {
      videoActionsRef.current = newVideoActions;
      setVideoState(newVideoState);
    },
    []
  );
  const handlePlayerError = useCallback((nextPlayerError) => {
    setPlayerError(nextPlayerError);
  }, []);

  const channel = view?.channel;

  useEffect(() => {
    setPlayerError(null);
    setVideoState(null);
  }, [content.id, channel?.configUrl, channel?.vlight]);

  // Player.js guarantees channel.configUrl and channel.vlight are present before rendering ChannelScreen
  if (!channel?.configUrl || !channel?.vlight) {
    return null;
  }

  if (playerError) {
    const translationKey = resolveChannelPlayerErrorTranslationKey(playerError);

    return (
      <ContentIsNotAvailableError
        title={t(`contentIsNotAvailable.${translationKey}.title`)}
        text={t(`contentIsNotAvailable.${translationKey}.text`)}
        button={null}
        content={content}
        closeRouteDialogButton={closeRouteDialogButton}
      />
    );
  }

  return (
    <Box
      data-testid="ChannelScreen"
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <ChannelPlayer
          contentId={content.id}
          configUrl={channel.configUrl}
          vlight={channel.vlight}
          fullscreenTarget={fullscreenTarget}
          autoPlay={autoPlay}
          muted={muted}
          hideTitle={hideTitle}
          onVideoStateChange={handleVideoStateChange}
          onError={handlePlayerError}
          viewDataTracker={viewDataTracker}
        />

        <ChannelScreenContent
          content={content}
          videoState={videoState}
          videoActions={videoActionsRef.current}
          sponsorCornerBannerPlace={sponsorCornerBannerPlace}
          sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
          sponsorPlayerLogoPlace={sponsorPlayerLogoPlace}
          sponsorPlayerButtonPlace={sponsorPlayerButtonPlace}
          sponsorVideoPlace={sponsorVideoPlace}
          fullscreenTarget={fullscreenTarget}
          controls={controls}
          muted={muted}
          autoPlay={autoPlay}
          closeRouteDialogButton={closeRouteDialogButton}
        />
      </Box>
    </Box>
  );
}

ChannelScreen.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CHANNEL]).isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    isAdvEnabled: PropTypes.bool,
    widget: PropTypes.object,
    sport: PropTypes.shape({
      id: PropTypes.string,
    }),
    rightholder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  view: PropTypes.shape({
    channel: PropTypes.shape({
      configUrl: PropTypes.string.isRequired,
      vlight: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  viewDataTracker: PropTypes.object.isRequired,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  hideTitle: PropTypes.bool,
  controls: PropTypes.bool,
  closeRouteDialogButton: PropTypes.node,
  fullscreenTarget: PropTypes.string,
  sponsorCornerBannerPlace: PropTypes.string,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorPlayerLogoPlace: PropTypes.string,
  sponsorPlayerButtonPlace: PropTypes.string,
  sponsorVideoPlace: PropTypes.string,
};
